import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Box, 
  Card, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Typography,
  Button,
  Chip,
  TextField,
  Grid,
  Paper,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Alert,
  Stack,
  Divider,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  CardContent,
  CardActions,
  Fab
} from '@mui/material';
import { 
  Add as AddIcon, 
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  Send as SendIcon,
  SwapHoriz as ConvertIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Assignment as DraftIcon,
  HourglassEmpty as ExpireIcon,
  Cancel as RejectIcon,
  ViewModule as CardViewIcon,
  ViewList as ListViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

import { estimateService } from '../../../services/estimateService';
import { EstimateStatus } from '../../../types/estimate';
import { usePermissions } from '../../../hooks/usePermissions';

// Indian currency formatter
const formatIndianCurrency = (amount: number) => 
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);

interface EstimateFilters {
  search_term?: string;
  status?: EstimateStatus[];
  estimate_date_from?: string;
  estimate_date_to?: string;
  customer_id?: string;
}

const EstimatesList: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const [page, setPage] = useState(1);
  const [perPage] = useState(12);
  const [filters, setFilters] = useState<EstimateFilters>({});
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEstimate, setSelectedEstimate] = useState<any | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['estimates', page, perPage, filters],
    queryFn: () => estimateService.getEstimates({
      page,
      per_page: perPage,
      ...filters
    }),
    placeholderData: (previousData) => previousData
  });

  const handleFilterChange = (key: keyof EstimateFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1); // Reset to first page when filtering
  };

  const getStatusConfig = (status: string) => {
    const statusMap: Record<string, { color: any, icon: JSX.Element, label: string }> = {
      draft: { color: 'default', icon: <DraftIcon fontSize="small" />, label: 'Draft' },
      sent: { color: 'info', icon: <SendIcon fontSize="small" />, label: 'Sent' },
      accepted: { color: 'success', icon: <CheckCircleIcon fontSize="small" />, label: 'Accepted' },
      rejected: { color: 'error', icon: <RejectIcon fontSize="small" />, label: 'Rejected' },
      expired: { color: 'warning', icon: <ExpireIcon fontSize="small" />, label: 'Expired' },
      converted: { color: 'secondary', icon: <ConvertIcon fontSize="small" />, label: 'Converted' },
    };
    return statusMap[status] || { color: 'default', icon: <ScheduleIcon fontSize="small" />, label: status };
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, estimate: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedEstimate(estimate);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEstimate(null);
  };

  const handleCardClick = (estimateId: string) => {
    navigate(`/admin/documents/estimates/${estimateId}`);
  };

  const handleDownloadPDF = async (estimateId: string) => {
    try {
      const blob = await estimateService.generatePDF(estimateId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `estimate-${selectedEstimate?.estimate_number || estimateId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('PDF downloaded successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to download PDF', { variant: 'error' });
    }
    handleMenuClose();
  };

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">Error loading estimates: {error.message}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Estimates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data?.total || 0} total estimates
          </Typography>
        </Box>
        {hasPermission('estimates.create') && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/documents/estimates/create')}
            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2 }}
          >
            Create Estimate
          </Button>
        )}
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search by estimate number, customer name..."
              value={filters.search_term || ''}
              onChange={(e) => handleFilterChange('search_term', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, newMode) => newMode && setViewMode(newMode)}
                size="small"
              >
                <ToggleButton value="card">
                  <CardViewIcon />
                </ToggleButton>
                <ToggleButton value="list">
                  <ListViewIcon />
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Estimates Grid/List */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Typography>Loading estimates...</Typography>
        </Box>
      ) : data?.estimates.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Alert severity="info">No estimates found</Alert>
        </Box>
      ) : viewMode === 'card' ? (
        <>
          <Grid container spacing={2}>
            {data?.estimates.map((estimate: any) => {
              const statusConfig = getStatusConfig(estimate.status);
              const isConverted = !!estimate.converted_to_order_id;

              return (
                <Grid item xs={12} sm={6} md={4} key={estimate.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      '&:hover': {
                        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                    onClick={() => handleCardClick(estimate.id)}
                  >
                    <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                      {/* Header with Estimate Number and Status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight={600} color="primary" gutterBottom>
                            {estimate.estimate_number}
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            <Chip
                              icon={statusConfig.icon}
                              label={statusConfig.label}
                              color={statusConfig.color}
                              size="small"
                              sx={{ borderRadius: 4 }}
                            />
                            {isConverted && (
                              <Chip
                                icon={<ConvertIcon fontSize="small" />}
                                label="Converted"
                                color="secondary"
                                size="small"
                                variant="outlined"
                                sx={{ borderRadius: 4 }}
                              />
                            )}
                          </Stack>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, estimate)}
                          sx={{ mt: -1, mr: -1 }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      {/* Customer Info */}
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                            <PersonIcon fontSize="small" />
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                              Customer
                            </Typography>
                            <Typography variant="body2" fontWeight={500} noWrap>
                              {estimate.customer_name || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Dates */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                Estimate Date
                              </Typography>
                            </Box>
                            <Typography variant="body2" fontWeight={500}>
                              {format(new Date(estimate.estimate_date), 'dd/MM/yyyy')}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                Valid Until
                              </Typography>
                            </Box>
                            <Typography variant="body2" fontWeight={500}>
                              {format(new Date(estimate.valid_until), 'dd/MM/yyyy')}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Amount */}
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <MoneyIcon sx={{ fontSize: 14, color: 'success.main' }} />
                            <Typography variant="caption" color="text.secondary">
                              Total Amount
                            </Typography>
                          </Box>
                          <Typography variant="h6" fontWeight={600} color="success.main">
                            {formatIndianCurrency(estimate.total_amount)}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>

                    <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                      <Button
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/documents/estimates/${estimate.id}`);
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        View
                      </Button>
                      {hasPermission('estimates.update') && (
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/documents/estimates/${estimate.id}/edit`);
                          }}
                          sx={{ textTransform: 'none' }}
                          disabled={estimate.status !== 'draft'}
                        >
                          Edit
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Pagination for Card View */}
          {data && data.total_pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  sx={{ borderRadius: 2 }}
                >
                  Previous
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                  <Typography variant="body2">
                    Page {page} of {data.total_pages}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  disabled={page === data.total_pages}
                  onClick={() => setPage(page + 1)}
                  sx={{ borderRadius: 2 }}
                >
                  Next
                </Button>
              </Stack>
            </Box>
          )}
        </>
      ) : (
        <>
          {/* List View */}
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#0d47a1' }}>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Estimate #</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Customer</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Valid Until</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Items</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">Amount</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.estimates.map((estimate: any) => {
                  const statusConfig = getStatusConfig(estimate.status);
                  return (
                    <TableRow 
                      key={estimate.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleCardClick(estimate.id)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="primary">
                          {estimate.estimate_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {estimate.customer_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {format(new Date(estimate.estimate_date), 'dd/MM/yyyy')}
                      </TableCell>
                      <TableCell>
                        {format(new Date(estimate.valid_until), 'dd/MM/yyyy')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={statusConfig.icon}
                          label={statusConfig.label}
                          color={statusConfig.color}
                          size="small"
                          sx={{ borderRadius: 4 }}
                        />
                      </TableCell>
                      <TableCell>
                        {estimate.items_count} items
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body2" 
                          fontWeight={600}
                          color="success.main"
                        >
                          {formatIndianCurrency(estimate.total_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => navigate(`/admin/documents/estimates/${estimate.id}`)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {hasPermission('estimates.update') && (
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/admin/documents/estimates/${estimate.id}/edit`)}
                                disabled={estimate.status !== 'draft'}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="More Actions">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuClick(e, estimate)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination for List View */}
          {data && data.total_pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={data.total_pages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: { borderRadius: 2, minWidth: 200 }
          }
        }}
      >
        <MenuItem onClick={() => {
          navigate(`/admin/documents/estimates/${selectedEstimate?.id}`);
          handleMenuClose();
        }}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        {hasPermission('estimates.update') && (
          <MenuItem
            onClick={() => {
              navigate(`/admin/documents/estimates/${selectedEstimate?.id}/edit`);
              handleMenuClose();
            }}
            disabled={selectedEstimate?.status !== 'draft'}
          >
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleDownloadPDF(selectedEstimate?.id)}>
          <ListItemIcon><PdfIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
        {selectedEstimate?.status === 'draft' && hasPermission('estimates.send') && (
          <MenuItem onClick={() => {
            navigate(`/admin/documents/estimates/${selectedEstimate?.id}`);
            handleMenuClose();
          }}>
            <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Send to Customer</ListItemText>
          </MenuItem>
        )}
        {selectedEstimate?.status === 'accepted' && !selectedEstimate?.converted_to_order_id && hasPermission('sales_orders.create') && (
          <MenuItem onClick={() => {
            navigate(`/admin/documents/estimates/${selectedEstimate?.id}`);
            handleMenuClose();
          }}>
            <ListItemIcon><ConvertIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Convert to Order</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Floating Action Button */}
      {hasPermission('estimates.create') && (
      <Fab
        color="secondary"
        aria-label="add"
        onClick={() => navigate('/admin/documents/estimates/create')}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
        }}
      >
        <AddIcon />
      </Fab>
      )}
    </Box>
  );
};

export default EstimatesList;